import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Home, BookOpen, Calendar, Video, Bell, Heart, Phone, Headphones, MonitorSmartphone } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { requestInstallPrompt, useInstallPromptAvailability } from './InstallPrompt';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const canInstallApp = useInstallPromptAvailability();

  const navItems = [
    { name: 'Kryefaqe', href: '#ballina', icon: Home, route: '/' },
    { name: 'Kurani', href: '#kurani', icon: BookOpen },
    { name: 'Ramazani', href: null, icon: Calendar, route: '/ramazani' },
    { name: 'Duatë', href: null, icon: BookOpen, route: '/duate' },
    { name: 'Ligjërata', href: null, icon: Headphones, route: '/ligjerate' },
    { name: 'Artikuj', href: '#shkrime', icon: BookOpen },
    { name: 'Video', href: '#video', icon: Video },
    { name: 'Njoftime', href: '#njoftime', icon: Bell },
    { name: 'Dhurime', href: '#dhurime', icon: Heart },
    { name: 'Kontakti', href: '#kontakti', icon: Phone },
  ];

  const handleNavClick = (item) => {
    setIsOpen(false);

    // If item has a dedicated route (like /ramazani), navigate to it
    if (item.route) {
      if (item.route === '/' && location.pathname === '/') {
        // Already on home, scroll to top or section
        if (item.href) {
          const element = document.querySelector(item.href);
          if (element) element.scrollIntoView({ behavior: 'smooth' });
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      } else if (item.route === '/') {
        // Navigate home then scroll
        navigate('/');
        if (item.href) {
          setTimeout(() => {
            const element = document.querySelector(item.href);
            if (element) element.scrollIntoView({ behavior: 'smooth' });
          }, 100);
        }
      } else {
        navigate(item.route);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      return;
    }

    // Section scroll (same page)
    if (item.href) {
      if (location.pathname !== '/') {
        // Navigate to home first, then scroll
        navigate('/');
        setTimeout(() => {
          const element = document.querySelector(item.href);
          if (element) element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      } else {
        const element = document.querySelector(item.href);
        if (element) element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleInstallClick = async () => {
    setIsOpen(false);
    await requestInstallPrompt();
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass" data-testid="navbar">
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <motion.div 
            className="flex items-center gap-3 cursor-pointer"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            onClick={() => { navigate('/'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          >
            <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-primary/20 bg-white shadow-sm">
              <img src="/logo192.png" alt="Xhamia Logo" className="w-full h-full object-cover" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-lg text-primary leading-tight">Xhamia</h2>
              <p className="text-xs text-muted-foreground">Ali Demit</p>
            </div>
          </motion.div>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-6">
            {navItems.map((item, index) => (
              <motion.button
                key={item.name}
                onClick={() => handleNavClick(item)}
                className="flex items-center gap-2 text-foreground hover:text-primary font-medium transition-colors duration-300"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                data-testid={`nav-link-${item.name.toLowerCase()}`}
              >
                <item.icon className="w-4 h-4" />
                {item.name}
              </motion.button>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-muted transition-colors"
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden border-t border-white/20"
            data-testid="mobile-menu"
          >
            <div className="container mx-auto px-4 py-4 space-y-2">
              {navItems.map((item) => (
                <button
                  key={item.name}
                  onClick={() => handleNavClick(item)}
                  className="flex items-center gap-3 w-full px-4 py-3 rounded-lg hover:bg-primary/10 transition-colors"
                  data-testid={`mobile-nav-link-${item.name.toLowerCase()}`}
                >
                  <item.icon className="w-5 h-5 text-primary" />
                  <span className="font-medium">{item.name}</span>
                </button>
              ))}
              {canInstallApp && (
                <button
                  onClick={handleInstallClick}
                  className="flex items-center gap-3 w-full px-4 py-3 rounded-lg bg-emerald-700 text-white hover:bg-emerald-800 transition-colors shadow-sm"
                  data-testid="mobile-install-app-button"
                >
                  <MonitorSmartphone className="w-5 h-5 text-white" />
                  <span className="font-medium">📲 Instalo Aplikacionin</span>
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
